import React from 'react';
import { Document, Page } from 'react-pdf';
import type { PdfTextSearchHit } from '../../utils/pdfTextSearch';
import { PdfSearchHits } from './PdfSearchHits';
import type { PdfJsLikeDoc } from './usePdfPaneView';

export function PdfViewerPane({
  file,
  canvasWrapRef,
  focused,
  onFocus,
  spaceHeld,
  loading,
  error,
  pageNumber,
  renderedPageNumber,
  onPageRenderSuccess,
  visualWidth,
  visualHeight,
  zoomRatio,
  baseScale,
  renderTextLayer,
  pageTransformZIndex,
  onDocumentLoadSuccess,
  onDocumentLoadError,
  onPointerMove,
  onPointerLeave,
  searchHits,
  activeSearchIndex,
  paneLabel,
  children,
  afterPage,
  wrapExtras,
}: {
  file: string;
  canvasWrapRef: React.Ref<HTMLDivElement>;
  focused?: boolean;
  onFocus?: () => void;
  spaceHeld?: boolean;
  loading: boolean;
  error: string | null;
  pageNumber: number;
  renderedPageNumber: number | null;
  onPageRenderSuccess: () => void;
  visualWidth: number;
  visualHeight: number;
  zoomRatio: number;
  baseScale: number;
  renderTextLayer: boolean;
  pageTransformZIndex: number;
  onDocumentLoadSuccess: (doc: PdfJsLikeDoc) => void | Promise<void>;
  onDocumentLoadError: (err: Error) => void;
  onPointerMove?: (e: React.PointerEvent<HTMLDivElement>) => void;
  onPointerLeave?: () => void;
  searchHits?: PdfTextSearchHit[];
  activeSearchIndex?: number;
  paneLabel?: React.ReactNode;
  children?: React.ReactNode;
  afterPage?: React.ReactNode;
  wrapExtras?: React.ReactNode;
}) {
  return (
    <div
      className={`pdf-viewer-pane${focused ? ' is-focused' : ''}`}
      onMouseDown={onFocus}
      onPointerDownCapture={onFocus}
    >
      {paneLabel ? <div className="pdf-viewer-pane-label">{paneLabel}</div> : null}
      <div
        className={`pdf-viewer-canvas-wrap${spaceHeld ? ' pdf-viewer-canvas-wrap--space-held' : ''}`}
        ref={canvasWrapRef}
        style={{ position: 'relative' }}
      >
        {loading && (
          <div className="pdf-viewer-loading">
            <span>Loading PDF…</span>
          </div>
        )}
        {error && (
          <div className="pdf-viewer-error">
            <span>{error}</span>
          </div>
        )}
        {!error && (
          <Document
            file={file}
            onLoadSuccess={doc => void onDocumentLoadSuccess(doc as PdfJsLikeDoc)}
            onLoadError={onDocumentLoadError}
            loading={null}
          >
            <div
              className="pdf-page-container"
              style={{ width: visualWidth, height: visualHeight, position: 'relative' }}
              onPointerMove={onPointerMove}
              onPointerLeave={onPointerLeave}
            >
              <div
                className="pdf-page-transform"
                style={{
                  ...(zoomRatio !== 1 ? { transform: `scale(${zoomRatio})`, transformOrigin: 'top left' } : {}),
                  zIndex: pageTransformZIndex,
                }}
              >
                {(() => {
                  const isNewPage = renderedPageNumber !== pageNumber;
                  const hasPrev = renderedPageNumber != null && isNewPage;
                  return (
                    <>
                      {hasPrev && (
                        <Page
                          key={`prev-${renderedPageNumber}`}
                          pageNumber={renderedPageNumber!}
                          scale={baseScale}
                          renderTextLayer={false}
                          renderAnnotationLayer={false}
                          className="pdf-page pdf-page-prev"
                        />
                      )}
                      <Page
                        key={`curr-${pageNumber}`}
                        pageNumber={pageNumber}
                        scale={baseScale}
                        renderTextLayer={renderTextLayer}
                        renderAnnotationLayer={false}
                        className={`pdf-page ${isNewPage ? 'pdf-page-loading' : ''}`}
                        onRenderSuccess={onPageRenderSuccess}
                      />
                    </>
                  );
                })()}
                {searchHits && searchHits.length > 0 ? (
                  <div className="pdf-search-hits-layer" aria-hidden>
                    <PdfSearchHits hits={searchHits} activeIndex={activeSearchIndex ?? 0} />
                  </div>
                ) : null}
                {children}
              </div>
              {afterPage}
            </div>
          </Document>
        )}
        {wrapExtras}
      </div>
    </div>
  );
}
